/**
 * Shared hook + provider for the Settings page: loads / saves the global
 * admin_settings doc once and exposes update + save helpers to every sub-tab.
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '../../api';
import { toast } from 'sonner';

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/settings')
            .then(r => setSettings(r.data))
            .finally(() => setLoading(false));
    }, []);

    const update = useCallback((k, v) => {
        setSettings(s => ({ ...s, [k]: v }));
    }, []);

    /**
     * Save a subset of fields. Pass the actual keys you want persisted as
     * an array, or omit to save the full known schema. `overrides` lets a
     * caller save a value that was just set via `update()` without waiting
     * for the state update to land first (state updates are async, so a
     * `save()` called right after `update()` would otherwise still read
     * the stale pre-update value).
     */
    const save = useCallback(async (keys, overrides) => {
        if (!settings) return;
        setSaving(true);
        try {
            const merged = overrides ? { ...settings, ...overrides } : settings;
            const payload = keys
                ? Object.fromEntries(keys.map(k => [k, normalizeValue(k, merged[k])]))
                : buildFullPayload(merged);
            await api.patch('/settings', payload);
            toast.success('Settings saved');
            if (settings.gl_enabled) {
                try { await api.post('/gl/_bootstrap'); } catch { /* non-fatal */ }
            }
            return true;
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || 'Save failed');
            return false;
        } finally {
            setSaving(false);
        }
    }, [settings]);

    return (
        <SettingsContext.Provider value={{ settings, loading, saving, update, save }}>
            {children}
        </SettingsContext.Provider>
    );
}

export function useSettings() {
    const ctx = useContext(SettingsContext);
    if (!ctx) throw new Error('useSettings must be used inside SettingsProvider');
    return ctx;
}

function normalizeValue(key, value) {
    const numKeys = ['default_vat_rate', 'default_payment_terms', 'default_domain_setup_fee', 'default_domain_monthly_support_fee', 'vat_flat_rate'];
    // Nullable optional numbers — must stay null when untouched, never silently
    // coerced to 0, since 0 is itself a meaningful override (bug: previously any
    // "Save all" on Company/Terms/Profitability zeroed these out for every client).
    const nullableNumKeys = ['profitability_credit_unit_cost', 'profitability_default_forecast_cost', 'profitability_trailing_window_months'];
    if (nullableNumKeys.includes(key)) {
        if (value === '' || value === null || value === undefined) return null;
        const n = parseFloat(value);
        return Number.isFinite(n) ? n : null;
    }
    if (numKeys.includes(key)) {
        const n = parseFloat(value);
        return Number.isFinite(n) ? n : 0;
    }
    if (key === 'notification_email_recipients' || key === 'notification_events' || key === 'support_internal_notify_emails') {
        if (Array.isArray(value)) return value;
        return (value || '').split(',').map(s => s.trim()).filter(Boolean);
    }
    if (key === 'reminder_days') {
        if (Array.isArray(value)) return value.map(n => parseInt(n, 10)).filter(Number.isFinite);
        return (value || '').split(',').map(s => parseInt(s.trim(), 10)).filter(Number.isFinite);
    }
    return value;
}

function buildFullPayload(s) {
    const keys = [
        'company_name', 'company_address', 'company_vat', 'company_logo_url',
        'brand_primary', 'brand_accent',
        'default_vat_rate', 'default_payment_terms', 'default_proposal_terms', 'default_contract_terms',
        'proposal_number_prefix', 'contract_number_prefix', 'invoice_number_prefix',
        'payment_link_default',
        'hosting_subdomain_suffix', 'default_domain_setup_fee', 'default_domain_monthly_support_fee',
        'hosting_blurb_subdomain', 'hosting_blurb_client_domain',
        'notification_email_enabled', 'notification_email_recipients', 'notification_email_sender_method', 'notification_events',
        'org_type', 'vat_registered', 'vat_scheme', 'vat_flat_rate', 'gl_enabled',
        'project_stage_sla',
        'crunch_bank_account',
        'support_from_email', 'support_internal_notify_emails', 'support_sla_hours',
        'profitability_credit_unit_cost', 'profitability_default_forecast_cost', 'profitability_trailing_window_months',
    ];
    return Object.fromEntries(keys.map(k => [k, normalizeValue(k, s[k])]));
}
